import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff, ImagePlus, Mic, Plus, RefreshCw, RotateCcw, Save, Search, Shuffle, Sparkles, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const themeStyle = (theme) => {
  const map = {
    kd9: "linear-gradient(150deg,#05070b 0%,#101d34 44%,#4b1b68 100%)",
    rider: "linear-gradient(150deg,#13243b 0%,#d2ad58 52%,#e7d8ad 100%)",
    egyptian: "linear-gradient(150deg,#050505 0%,#4a3019 48%,#b88736 100%)",
    marseille: "linear-gradient(150deg,#1b1830 0%,#7a1630 50%,#d5b35a 100%)",
    custom: "linear-gradient(150deg,#090b12 0%,#173d5a 50%,#542569 100%)",
  };
  return map[theme] || map.custom;
};

const deckTheme = (slug = "") => (
  slug.includes("egip") ? "egyptian" :
  slug.includes("mars") ? "marseille" :
  slug.includes("rider") ? "rider" : "kd9"
);

const TAROT_BACK_IMAGES = Object.freeze({
  "rider-waite": "/assets/oracle/rider-waite-back.svg",
  "tarot-egipcio": "/assets/oracle/tarot-egipcio-back.svg",
  "tarot-marsella": "/assets/oracle/tarot-marsella-back.svg",
});

const deckBackImage = (deck = {}) => {
  const slug = deck?.slug || deck?.deck_slug || "";
  if (TAROT_BACK_IMAGES[slug]) return TAROT_BACK_IMAGES[slug];
  const theme = deck?.theme || deckTheme(slug);
  const byTheme = {
    rider: TAROT_BACK_IMAGES["rider-waite"],
    egyptian: TAROT_BACK_IMAGES["tarot-egipcio"],
    marseille: TAROT_BACK_IMAGES["tarot-marsella"],
  };
  return byTheme[theme] || null;
};

const deckBackStyle = (deck = {}) => {
  const image = deckBackImage(deck);
  if (!image) {
    const slug = deck?.slug || deck?.deck_slug || "";
    return { background: themeStyle(deck?.theme || deckTheme(slug)) };
  }
  return {
    backgroundColor: "#050507",
    backgroundImage: `url("${image}")`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  };
};

const ORACLE_VOICE_LIMIT_SECONDS = 180;

const claimStatusLabel = {
  claimed: "Reservada",
  answered: "Respondida",
  expired: "Reserva expirada",
};

const OracleTabs = ({ items, active, onChange, columns = "grid-cols-3" }) => (
  <nav className={`grid ${columns} gap-2 mb-6`} aria-label="Navegación del Oráculo">
    {items.map(([key, label]) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        aria-current={active === key ? "page" : undefined}
        className={
          (active === key ? "kd9-btn-primary" : "kd9-btn-outline") +
          " min-h-11 min-w-0 px-2 sm:px-4 py-2 rounded-lg text-[10px] sm:text-xs leading-tight text-center whitespace-normal break-words"
        }
      >
        {label}
      </button>
    ))}
  </nav>
);

const DirectorOracleTabs = ({ active, onChange }) => {
  const items = [
    ["resumen", "Resumen"],
    ["economia", "Economía"],
    ["mazos", "Tarot"],
    ["biblioteca", "Biblioteca"],
    ["consultas", "Consultas"],
    ["metodos", "Métodos"],
  ];
  return (
    <nav
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6"
      aria-label="Gobierno del Oráculo"
      data-testid="oracle-director-tabs"
    >
      {items.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          aria-current={active === key ? "page" : undefined}
          className={
            (active === key ? "kd9-btn-primary" : "kd9-btn-outline") +
            " w-full min-w-0 min-h-[52px] rounded-xl px-2.5 py-3 text-[9px] sm:text-[10px] leading-tight text-center whitespace-normal break-words overflow-hidden"
          }
          style={{ letterSpacing: ".06em" }}
        >
          {label}
        </button>
      ))}
    </nav>
  );
};

const DeckCover = ({ deck, className = "h-40" }) => (
  <div className={`relative overflow-hidden ${className} flex items-center justify-center`} style={{background:themeStyle(deck?.theme),color:"var(--kd9-gold)"}}>
    <div className="text-6xl">{deck?.back_symbol || "✦"}</div>
    {deck?.cover_image_url && <img src={deck.cover_image_url} alt={`Portada ${deck.name || "Tarot"}`} className="absolute inset-0 h-full w-full object-cover" onError={e=>{e.currentTarget.style.display="none";}} />}
    {deck?.cover_image_url && <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/5 pointer-events-none" />}
  </div>
);

const CardFace = ({ card, deck, small = false, hidden = false, selected = false, onClick, fluid = false }) => {
  const face = hidden ? null : card;
  const ratio = small ? "86 / 132" : "132 / 204";
  const backImage = hidden ? deckBackImage(deck) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      aria-label={hidden ? "Carta boca abajo" : `${face?.name || "Carta"} · ${face?.orientation === "reversed" ? "Invertida" : "Derecho"}`}
      className={
        "relative shrink-0 rounded-xl border overflow-hidden transition-all duration-200 " +
        (onClick ? "cursor-pointer hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-amber-300 " : "cursor-default ") +
        (selected ? "-translate-y-2 scale-[1.03] " : "")
      }
      style={{
        width: fluid ? "100%" : (small ? 86 : 132),
        maxWidth: small ? 86 : 132,
        height: fluid ? "auto" : (small ? 132 : 204),
        aspectRatio: fluid ? ratio : undefined,
        borderColor: selected ? "var(--kd9-gold)" : "rgba(212,175,55,.34)",
        boxShadow: selected ? "0 0 28px rgba(212,175,55,.3)" : "0 12px 35px rgba(0,0,0,.32)",
        ...(hidden ? deckBackStyle(deck) : { background: themeStyle(deck?.theme) }),
      }}
    >
      <div className="absolute inset-2 rounded-lg border z-[2] pointer-events-none" style={{ borderColor: "rgba(255,255,255,.17)" }} />
      {hidden ? (
        !backImage && <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <div className={small ? "text-2xl" : "text-4xl"} style={{ color: "var(--kd9-gold)" }}>{deck?.back_symbol || "✦"}</div>
          {!small && <div className="font-cinzel text-[9px] tracking-[.3em] text-white/70">KHAOS DELTA 9</div>}
        </div>
      ) : (
        <>
          <div className="absolute inset-0 flex items-center justify-center z-0">
            <div className={small ? "text-3xl" : "text-5xl"} style={{ color: "var(--kd9-gold)" }}>{face?.art_symbol || deck?.back_symbol || "✦"}</div>
          </div>
          {face?.image_url && (
            <img
              src={face.image_url}
              alt={face.name}
              className="absolute inset-0 w-full h-full object-cover z-[1]"
              style={{ transform: face?.orientation === "reversed" ? "rotate(180deg)" : "none" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <div className="absolute left-2 right-2 top-2 z-[3] rounded-md px-1.5 py-1 text-center backdrop-blur-sm" style={{background:"rgba(3,6,12,.72)"}}>
            <div className="font-mont text-[7px] sm:text-[8px] uppercase tracking-[.12em] text-white/80 truncate">{face?.position_label || "ARCANO"}</div>
          </div>
          <div className="absolute left-2 right-2 bottom-2 z-[3] rounded-md px-1.5 py-1.5 text-center backdrop-blur-sm" style={{background:"rgba(3,6,12,.82)"}}>
            <div className={"font-cinzel text-white leading-tight " + (small ? "text-[9px]" : "text-xs sm:text-sm")}>{face?.name}</div>
            <div className="font-mont text-[7px] sm:text-[8px] uppercase mt-1" style={{ color: face?.orientation === "reversed" ? "#c5a5f4" : "#9bdff4" }}>
              {face?.orientation === "reversed" ? "↓ Invertida" : "↑ Derecho"}
            </div>
          </div>
        </>
      )}
    </button>
  );
};

const TarotShuffle = ({ deck }) => (
  <div className="kd9-card p-10 md:p-14 text-center overflow-hidden">
    <style>{`
      @keyframes kd9TarotShuffle {
        0% { transform: translate3d(-34px, 8px, 0) rotate(var(--r1)); }
        50% { transform: translate3d(34px, -8px, 0) rotate(0deg); }
        100% { transform: translate3d(-8px, 10px, 0) rotate(var(--r2)); }
      }
      @keyframes kd9OracleGlow {
        0%,100% { opacity:.32; transform:scale(.94); }
        50% { opacity:.72; transform:scale(1.08); }
      }
    `}</style>
    <div className="relative h-56 md:h-64 max-w-sm mx-auto flex items-center justify-center">
      <div
        className="absolute w-48 h-48 rounded-full blur-3xl"
        style={{ background: "rgba(212,175,55,.14)", animation: "kd9OracleGlow 1.4s ease-in-out infinite" }}
      />
      {Array.from({ length: 9 }).map((_, i) => {
        const r1 = `${-20 + i * 5}deg`;
        const r2 = `${18 - i * 4}deg`;
        return <div
          key={i}
          className="absolute rounded-xl border"
          style={{
            width: 86, height: 132,
            borderColor: "rgba(212,175,55,.38)",
            ...deckBackStyle(deck),
            boxShadow: "0 18px 35px rgba(0,0,0,.35)",
            animation: `kd9TarotShuffle .82s ease-in-out ${i * .055}s infinite alternate`,
            "--r1": r1, "--r2": r2,
            zIndex: i,
          }}
        >
          <div className="absolute inset-2 rounded-lg border" style={{ borderColor: "rgba(255,255,255,.15)" }} />
          {!deckBackImage(deck) && <div className="absolute inset-0 flex items-center justify-center text-3xl" style={{ color: "var(--kd9-gold)" }}>{deck?.back_symbol || "✦"}</div>}
        </div>;
      })}
    </div>
    <div className="font-cinzel kd9-gold-text text-xl">BARAJANDO EL ORÁCULO...</div>
    <p className="font-cormorant text-lg mt-2" style={{ color: "var(--kd9-text-soft)" }}>
      Las 78 cartas se están reordenando. Cada barajada cambia el orden y la orientación antes de elegir.
    </p>
  </div>
);

const TarotUFan = ({ count, deck, selected, onChoose, disabled }) => {
  const labels = ["RAÍZ", "ENERGÍA", "PROYECCIÓN"];
  const total = Math.max(1, count || 78);
  const perRing = Math.ceil(total / 3);
  return (
    <div className="kd9-card overflow-hidden">
      <div className="px-4 sm:px-5 pt-5 text-center">
        <div className="font-mont text-[9px] sm:text-[10px] uppercase tracking-[.18em] sm:tracking-[.28em]" style={{ color: "var(--kd9-text-soft)" }}>
          Baraja completa · {total} cartas
        </div>
        <div className="font-cinzel kd9-gold-text text-base sm:text-lg mt-1">ELIGE DIRECTAMENTE SOBRE LA U</div>
        <p className="font-mont text-[9px] mt-2 sm:hidden" style={{color:"var(--kd9-text-soft)"}}>Desliza horizontalmente para recorrer toda la baraja.</p>
      </div>
      <div className="overflow-x-auto overflow-y-hidden overscroll-x-contain px-2 pb-4" data-testid="oracle-u-scroll">
        <div
          className="relative mx-auto"
          style={{ minWidth: 980, width: "100%", maxWidth: 1180, height: 560 }}
          data-testid="oracle-card-u-fan"
        >
          <div
            className="absolute left-1/2 top-[46%] -translate-x-1/2 rounded-full pointer-events-none"
            style={{
              width: 700, height: 315,
              border: "1px solid rgba(212,175,55,.12)",
              boxShadow: "0 0 90px rgba(91,33,182,.08) inset",
            }}
          />
          {Array.from({ length: total }).map((_, i) => {
            const ring = Math.min(2, Math.floor(i / perRing));
            const ringStart = ring * perRing;
            const ringCount = Math.min(perRing, total - ringStart);
            const pos = i - ringStart;
            const t = ringCount <= 1 ? 0 : (pos / (ringCount - 1)) * 2 - 1;
            const left = 5 + ((t + 1) / 2) * 90;
            const top = (11 + ring * 13) + (54 - ring * 7) * (1 - t * t);
            const rotation = t * (36 - ring * 4);
            const pick = selected.indexOf(i);
            const isSelected = pick >= 0;
            return (
              <button
                key={i}
                type="button"
                disabled={disabled || isSelected || selected.length >= 3}
                onClick={() => onChoose(i)}
                aria-label={isSelected ? `Carta ${i + 1}, elegida como ${labels[pick]}` : `Elegir carta ${i + 1}`}
                className="absolute rounded-lg border transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:cursor-default"
                style={{
                  width: 54, height: 86,
                  left: `${left}%`, top: `${top}%`,
                  transform: `translate(-50%,-50%) rotate(${rotation}deg) ${isSelected ? "translateY(-18px) scale(1.08)" : ""}`,
                  transformOrigin: "50% 88%",
                  borderColor: isSelected ? "var(--kd9-gold)" : "rgba(212,175,55,.32)",
                  ...deckBackStyle(deck),
                  boxShadow: isSelected ? "0 0 26px rgba(212,175,55,.55)" : "0 10px 22px rgba(0,0,0,.34)",
                  zIndex: isSelected ? 300 + pick : 20 + ring * 100 + pos,
                  opacity: disabled && !isSelected ? .72 : 1,
                }}
              >
                <div className="absolute inset-1.5 rounded-md border" style={{ borderColor: "rgba(255,255,255,.14)" }} />
                {!deckBackImage(deck) && <div className="absolute inset-0 flex items-center justify-center text-lg" style={{ color: "var(--kd9-gold)" }}>{deck?.back_symbol || "✦"}</div>}
                {isSelected && <div
                  className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-2 py-1 font-mont text-[8px] font-bold"
                  style={{ background: "var(--kd9-gold)", color: "#111" }}
                >{pick + 1} · {labels[pick]}</div>}
              </button>
            );
          })}
          <div className="absolute left-1/2 bottom-5 -translate-x-1/2 text-center pointer-events-none rounded-xl px-4 py-2" style={{background:"rgba(3,6,12,.82)"}}>
            <div className="font-cinzel text-sm kd9-gold-text">{selected.length}/3 ELEGIDAS</div>
            <div className="font-mont text-[9px] mt-1" style={{ color: "var(--kd9-text-soft)" }}>Toca cualquier carta visible del abanico</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConsultationCard = ({ item, onResonate, resonatingId, directorActions }) => (
  <article className="kd9-card p-5 md:p-6 space-y-5">
    <div className="flex flex-wrap items-center gap-3">
      <span className="font-mont text-[10px] uppercase tracking-[.2em] px-2 py-1 rounded-full border border-white/10">{item.synthetic ? "PULSO KD9 · SISTEMA" : item.deck_name}</span>
      <span className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>{item.public_alias}</span>
      <span className="ml-auto font-cinzel kd9-gold-text">{item.response_count}/{item.max_responses}</span>
    </div>
    <p className="font-cormorant text-xl md:text-2xl">{item.question}</p>
    <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-sm">
      {(item.cards || []).map((c) => <CardFace key={c.position} card={c} deck={{ theme: deckTheme(item.deck_slug) }} small fluid />)}
    </div>
    {!!item.responses?.length && <div className="space-y-3 pt-2">
      {item.responses.map((r) => (
        <div key={r.id} className="rounded-xl border p-4" style={{ borderColor: "var(--kd9-border)", background: "rgba(0,0,0,.14)" }}>
          <div className="font-cinzel kd9-gold-text text-sm mb-2">{r.consultant_name}</div>
          {r.audio_url ? <div className="space-y-3">
            <div className="font-mont text-[9px] uppercase tracking-[.18em]" style={{color:"var(--kd9-text-soft)"}}>🎙️ Respuesta de voz · {r.audio_duration_seconds ? `${Math.floor(r.audio_duration_seconds/60)}:${String(r.audio_duration_seconds%60).padStart(2,"0")}` : "Audio"}</div>
            <audio controls preload="metadata" src={r.audio_url} className="w-full" data-testid={`oracle-response-audio-${r.id}`}>
              Tu navegador no puede reproducir esta respuesta de voz.
            </audio>
            {r.transcript&&<div className="rounded-xl border border-white/10 p-3"><div className="font-mont text-[9px] uppercase opacity-60">Transcripción / resumen</div><p className="font-cormorant text-lg mt-1 whitespace-pre-wrap">{r.transcript}</p></div>}
          </div> : r.answer ? <p className="font-cormorant text-lg whitespace-pre-wrap">{r.answer}</p> : null}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-3">
            {onResonate && <button type="button" disabled={resonatingId===r.id} onClick={() => onResonate(r.id)} className="kd9-btn-outline min-h-11 px-3 py-2 rounded-lg text-[10px] w-full sm:w-auto disabled:opacity-50">⚡ Resonó conmigo · {r.resonances || 0}</button>}
            {directorActions && <button type="button" disabled={!!directorActions.busy} onClick={() => directorActions.response(r.id, !r.hidden)} className="kd9-btn-outline min-h-11 px-3 py-2 rounded-lg text-[10px] w-full sm:w-auto disabled:opacity-50">{r.hidden ? "Mostrar interpretación" : "Ocultar interpretación"}</button>}
          </div>
        </div>
      ))}
    </div>}
        {directorActions && !item.synthetic && <button type="button" disabled={!!directorActions.busy} onClick={() => directorActions.consultation(item.id, item.visible !== false)} className="kd9-btn-outline min-h-11 px-3 py-2 rounded-lg text-[10px] inline-flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50">{item.visible === false ? <Eye size={12}/> : <EyeOff size={12}/>} {item.visible === false ? "Mostrar consulta" : "Ocultar consulta"}</button>}
  </article>
);

const oracleOperationKey = (sessionId) => {
  const storageKey = `kd9-oracle-consultation:${sessionId}`;
  const stored = window.sessionStorage.getItem(storageKey);
  if (stored) return stored;
  const value = globalThis.crypto?.randomUUID?.() || `oracle-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.sessionStorage.setItem(storageKey, value);
  return value;
};

export const OracleUser = () => {
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, question_cost: 0, min_shuffles: 4, unit: "KD" });
  const [tab, setTab] = useState("consultar");
  const [deck, setDeck] = useState(null);
  const [session, setSession] = useState(null);
  const [selected, setSelected] = useState([]);
  const [revealed, setRevealed] = useState(null);
  const [mixing, setMixing] = useState(false);
  const [question, setQuestion] = useState("");
  const [category, setCategory] = useState("general");
  const [anonymous, setAnonymous] = useState(false);
  const [wall, setWall] = useState([]);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const publishBusyRef = useRef(false);
  const [resonatingId, setResonatingId] = useState(null);
  const [preflight, setPreflight] = useState({ can_ask: true, balance: 0, question_cost: 0, unit: "KD", reason: null });

  const load = useCallback(async () => {
    try {
      const [d, s, w, m, p] = await Promise.all([
        api.get("/api/oracle/decks"), api.get("/api/oracle/settings"),
        api.get("/api/oracle/wall"), api.get("/api/oracle/mine"),
        api.get("/api/oracle/preflight"),
      ]);
      setDecks(d.data); setSettings(s.data); setWall(w.data); setMine(m.data); setPreflight(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetReading = () => {
    if (busy) return;
    setSession(null);
    setSelected([]);
    setRevealed(null);
    setDeck(null);
    setQuestion("");
    setCategory("general");
    setAnonymous(false);
    setMixing(false);
  };

  const start = async (d) => {
    if (!settings.enabled || preflight.reason === "ORACLE_DISABLED") return toast.error("El Oráculo está temporalmente desactivado.");
    if (!preflight.can_ask) {
      if (preflight.reason === "INSUFFICIENT_ENERGY") {
        toast.error(`Necesitas ⚡ ${preflight.question_cost} ${preflight.unit || "KD"} para hacer esta pregunta.`);
        navigate("/app/energia-kd?from=oraculo");
        return;
      }
      if (preflight.reason === "WALLET_FROZEN") return toast.error("Tu billetera de Energía KD9 no está disponible.");
      return toast.error("No puedes iniciar una consulta en este momento.");
    }
    setBusy(true); setSession(null); setDeck(d); setSelected([]); setRevealed(null); setQuestion(""); setMixing(false);
    try {
      const { data } = await api.post("/api/oracle/tarot/session", { deck_slug: d.slug });
      setSession(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const shuffleDeck = async () => {
    if (!session || selected.length > 0 || revealed || busy) return;
    setBusy(true);
    setMixing(true);
    try {
      const request = api.post(`/api/oracle/tarot/session/${session.session_id}/shuffle`);
      const [, response] = await Promise.all([
        new Promise(resolve => setTimeout(resolve, 720)),
        request,
      ]);
      const data = response.data;
      setSession(current => ({ ...current, ...data }));
      if (data.ready_to_select && Number(data.shuffle_count) === Number(data.min_shuffles)) {
        toast.success("Barajado mínimo completo. Ya puedes elegir tus tres cartas.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMixing(false); setBusy(false); }
  };

  const choose = async (index) => {
    if (!session || revealed || Number(session.shuffle_count || 0) < Number(session.min_shuffles || 4) || selected.includes(index) || selected.length >= 3) return;
    const next = [...selected, index];
    setSelected(next);
    if (next.length === 3) {
      setBusy(true);
      try {
        const { data } = await api.post(`/api/oracle/tarot/session/${session.session_id}/reveal`, { selected_slots: next });
        setTimeout(() => setRevealed(data), 380);
      } catch (e) { toast.error(formatApiError(e)); setSelected([]); }
      finally { setBusy(false); }
    }
  };

  const publish = async () => {
    if (question.trim().length < 8) return toast.error("Formula una pregunta un poco más completa.");
    if (!session?.session_id) return toast.error("La sesión del Tarot ya no está disponible.");
    if (publishBusyRef.current) return;
    publishBusyRef.current = true;
    setBusy(true);
    const operationKey = oracleOperationKey(session.session_id);
    try {
      await api.post("/api/oracle/consultations", {
        session_id: session.session_id, question: question.trim(), category, anonymous,
        idempotency_key: operationKey,
      });
      window.sessionStorage.removeItem(`kd9-oracle-consultation:${session.session_id}`);
      toast.success("Tu consulta ya está en el Muro del Oráculo.");
      setSession(null); setRevealed(null); setSelected([]); setDeck(null); setQuestion("");
      await load(); setTab("mis");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { publishBusyRef.current = false; setBusy(false); }
  };

  const resonate = async (responseId) => {
    if (resonatingId) return;
    setResonatingId(responseId);
    try { await api.post(`/api/oracle/responses/${responseId}/resonate`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setResonatingId(null); }
  };

  return <PageShell kicker="KHAOS DELTA 9 · Interpretación" title="ORÁCULO KD9" desc="El Tarot abre la primera puerta del Oráculo. Elige tu mazo, mezcla, selecciona tres cartas y formula tu pregunta para recibir hasta cuatro interpretaciones públicas." testId="oracle-user-page">
    <button type="button" onClick={() => navigate("/app/numerologia")} className="kd9-btn-outline mb-4 min-h-12 rounded-xl px-5" data-testid="oracle-numerology-link">🔢 Numerología Viva KD9 · 100 ⚡ / 7 días</button>
    <OracleTabs
      items={[["consultar","Consultar"],["muro","Muro del Oráculo"],["mis","Mis consultas"]]}
      active={tab}
      onChange={setTab}
      columns="grid-cols-3"
    />

    {tab === "consultar" && <div className="space-y-6">
      {(!settings.enabled || preflight.reason === "ORACLE_DISABLED") && <div className="kd9-card p-5 border" style={{borderColor:"rgba(245,158,11,.35)"}}>
        <div className="font-cinzel kd9-gold-text">ORÁCULO TEMPORALMENTE DESACTIVADO</div>
        <p className="font-cormorant mt-2" style={{color:"var(--kd9-text-soft)"}}>Puedes consultar el Muro y tus lecturas, pero no iniciar una tirada nueva hasta que Dirección lo active.</p>
      </div>}
      {!session && !mixing && <>
        <div className="kd9-card p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1"><div className="font-cinzel kd9-gold-text text-lg sm:text-xl">ELIGE TU TAROT</div><p className="font-cormorant text-base sm:text-lg mt-2" style={{color:"var(--kd9-text-soft)"}}>Cada mazo utiliza la misma estructura de tres posiciones, con identidad visual y biblioteca interpretativa propia.</p></div>
            <div className="font-mont text-[10px] sm:text-xs">Saldo: <span className="kd9-gold-text">⚡ {preflight.balance} {preflight.unit || "KD"}</span><br/>Costo vigente: <span className="kd9-gold-text">{settings.question_cost ? `⚡ ${settings.question_cost} KD` : "GRATIS"}</span></div>
          </div>
          {!preflight.can_ask && preflight.reason === "INSUFFICIENT_ENERGY" && <button type="button" onClick={()=>navigate("/app/energia-kd?from=oraculo")} className="kd9-btn-primary min-h-11 px-5 py-3 rounded-lg text-[10px] mt-4">RECARGAR ENERGÍA KD9</button>}
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">{decks.map(d => <button type="button" key={d.slug} disabled={busy || !settings.enabled || !preflight.can_ask} onClick={() => start(d)} className="kd9-card text-left overflow-hidden group min-h-11 disabled:opacity-50 disabled:cursor-not-allowed">
          <DeckCover deck={d} className="h-40" />
          <div className="p-4 sm:p-5"><div className="font-cinzel kd9-gold-text text-base sm:text-lg">{d.name}</div><p className="font-cormorant mt-2 sm:min-h-[5rem]" style={{color:"var(--kd9-text-soft)"}}>{d.description}</p><div className="font-mont text-[10px] mt-3">{d.card_count} cartas · {d.allow_reversed ? "Derechas e invertidas" : "Solo derechas"}</div>{d.art_edition&&<div className="font-mont text-[9px] mt-2 opacity-60">{d.art_edition} · {d.art_license||"Colección KD9"}</div>}</div>
        </button>)}</div>
      </>}

      {mixing && <TarotShuffle deck={deck} />}

      {session && !mixing && !revealed && <div className="space-y-5">
        <div className="kd9-card p-5 sm:p-6 text-center">
          <div className="font-cinzel kd9-gold-text text-lg sm:text-xl">
            {Number(session.shuffle_count || 0) >= Number(session.min_shuffles || 4) ? "BARAJA LISTA · ELIGE 3 CARTAS" : "BARAJA LAS CARTAS ANTES DE ELEGIR"}
          </div>
          <p className="font-cormorant mt-2">
            Debes barajar este Tarot un mínimo de {session.min_shuffles || settings.min_shuffles || 4} veces por pregunta. Toda la baraja se despliega boca abajo en forma de U después del barajado para que escojas tú mismo las tres cartas.
          </p>
          <div className="mx-auto mt-4 max-w-md">
            <div className="flex items-center justify-between gap-3 font-mont text-[10px] uppercase">
              <span>Barajadas realizadas</span>
              <strong className="kd9-gold-text">{session.shuffle_count || 0}/{session.min_shuffles || settings.min_shuffles || 4}</strong>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min(100, (Number(session.shuffle_count || 0) / Number(session.min_shuffles || settings.min_shuffles || 4)) * 100)}%`,
                  background: "linear-gradient(90deg,var(--kd9-gold),var(--kd9-gold-2))",
                }}
              />
            </div>
          </div>
          {selected.length === 0 && <button
            type="button"
            disabled={busy}
            onClick={shuffleDeck}
            className="kd9-btn-primary min-h-12 px-6 py-3 rounded-xl text-xs mt-5 w-full sm:w-auto disabled:opacity-50"
            data-testid="oracle-shuffle-btn"
          >
            <Shuffle size={15} className="inline mr-2"/>
            {Number(session.shuffle_count || 0) >= Number(session.min_shuffles || 4) ? "BARAJAR OTRA VEZ" : `BARAJAR · ${Number(session.shuffle_count || 0) + 1}ª`}
          </button>}
          <div className="font-mont text-[10px] sm:text-xs mt-4">1ª Raíz · 2ª Energía actual · 3ª Proyección</div>
          <button type="button" disabled={busy} onClick={resetReading} className="kd9-btn-outline min-h-11 px-4 py-2 rounded-lg text-[10px] mt-4 w-full sm:w-auto">CAMBIAR TAROT</button>
        </div>
        {Number(session.shuffle_count || 0) >= Number(session.min_shuffles || 4) && <TarotUFan
          count={session.slot_count}
          deck={session.deck}
          selected={selected}
          onChoose={choose}
          disabled={busy}
        />}
      </div>}

      {revealed && <div className="space-y-6">
        <div className="kd9-card p-4 sm:p-6 text-center">
          <div className="font-cinzel kd9-gold-text text-lg sm:text-xl mb-5">TUS TRES CARTAS HAN SIDO REVELADAS</div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-md mx-auto">{revealed.cards.map(c => <CardFace key={c.position} card={c} deck={revealed.deck} fluid />)}</div>
          <button type="button" disabled={busy} onClick={resetReading} className="kd9-btn-outline min-h-11 px-4 py-2 rounded-lg text-[10px] mt-5">NUEVA TIRADA</button>
        </div>
        <div className="kd9-card p-4 sm:p-6">
          <div className="font-cinzel kd9-gold-text text-lg sm:text-xl">AHORA FORMULA TU PREGUNTA</div>
          <p className="font-cormorant mt-2 mb-5" style={{color:"var(--kd9-text-soft)"}}>Será pública junto con tus tres cartas. Evita nombres completos, teléfonos, direcciones, documentos o información privada.</p>
          <textarea value={question} onChange={e=>setQuestion(e.target.value)} maxLength={500} rows={5} className="kd9-input w-full p-4 resize-y" placeholder="¿Qué deseas comprender?" />
          <div className="font-mont text-[9px] text-right mt-1" style={{color:"var(--kd9-text-soft)"}}>{question.length}/500</div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <select value={category} onChange={e=>setCategory(e.target.value)} className="kd9-input px-3 py-3">
              <option value="general">General</option><option value="amor">Amor</option><option value="familia">Familia</option><option value="trabajo">Trabajo</option><option value="dinero">Dinero</option><option value="decisiones">Decisiones</option><option value="crecimiento">Crecimiento personal</option>
            </select>
            <label className="kd9-card px-4 py-3 flex items-center gap-3 font-mont text-xs"><input type="checkbox" checked={anonymous} onChange={e=>setAnonymous(e.target.checked)}/> Publicar como Anónimo KD9</label>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="font-cinzel text-lg">Costo: <span className="kd9-gold-text">{settings.question_cost ? `⚡ ${settings.question_cost} KD` : "GRATIS"}</span></div>
            <button type="button" disabled={busy || question.trim().length < 8} onClick={publish} className="kd9-btn-primary min-h-11 px-6 py-3 rounded-lg text-xs w-full sm:w-auto sm:ml-auto disabled:opacity-50 disabled:cursor-not-allowed"><Sparkles size={14} className="inline mr-2"/>PUBLICAR EN EL ORÁCULO</button>
          </div>
        </div>
      </div>}
    </div>}

    {tab === "muro" && <div className="space-y-5">
      <div className="flex items-center justify-between gap-3"><div className="font-cinzel kd9-gold-text">MURO DEL ORÁCULO</div><button type="button" disabled={busy} onClick={load} className="kd9-btn-outline min-h-11 px-3 py-2 rounded-lg text-[10px] inline-flex items-center gap-2"><RefreshCw size={13}/>Actualizar</button></div>
      {wall.map(x => <ConsultationCard key={x.id} item={x} onResonate={x.synthetic ? null : resonate} resonatingId={resonatingId} />)}{!wall.length&&<div className="kd9-card p-8 sm:p-10 text-center">El Pulso del Oráculo se está actualizando.</div>}
    </div>}
    {tab === "mis" && <div className="space-y-5">
      <div className="flex items-center justify-between gap-3"><div className="font-cinzel kd9-gold-text">MIS CONSULTAS</div><button type="button" disabled={busy} onClick={load} className="kd9-btn-outline min-h-11 px-3 py-2 rounded-lg text-[10px] inline-flex items-center gap-2"><RefreshCw size={13}/>Actualizar</button></div>
      {mine.map(x => <ConsultationCard key={x.id} item={x} onResonate={resonate} resonatingId={resonatingId} />)}{!mine.length&&<div className="kd9-card p-8 sm:p-10 text-center">Todavía no has realizado consultas.</div>}
    </div>}
  </PageShell>;
};

export const OracleConsultant = () => {
  const [tab,setTab]=useState("available");
  const [available,setAvailable]=useState([]);
  const [mine,setMine]=useState([]);
  const [workspace,setWorkspace]=useState(null);
  const [guide,setGuide]=useState(null);
  const [answer,setAnswer]=useState("");
  const [answerMode,setAnswerMode]=useState("text");
  const [voiceBlob,setVoiceBlob]=useState(null);
  const [voiceUrl,setVoiceUrl]=useState("");
  const [voiceSeconds,setVoiceSeconds]=useState(0);
  const [voiceTranscript,setVoiceTranscript]=useState("");
  const [recording,setRecording]=useState(false);
  const [busy,setBusy]=useState(false);
  const [claimIssue,setClaimIssue]=useState(false);
  const recorderRef=useRef(null);
  const streamRef=useRef(null);
  const voiceChunksRef=useRef([]);
  const voiceSecondsRef=useRef(0);
  const voiceTimerRef=useRef(null);
  const voiceUrlRef=useRef("");

  const releaseVoiceStream=()=>{
    if(streamRef.current){
      streamRef.current.getTracks().forEach(track=>track.stop());
      streamRef.current=null;
    }
  };
  const clearVoiceTimer=()=>{
    if(voiceTimerRef.current){
      window.clearInterval(voiceTimerRef.current);
      voiceTimerRef.current=null;
    }
  };
  const resetVoice=()=>{
    clearVoiceTimer();
    const recorder=recorderRef.current;
    if(recorder&&recorder.state!=="inactive"){
      try{recorder.stop();}catch{}
    }
    recorderRef.current=null;
    releaseVoiceStream();
    if(voiceUrlRef.current){
      URL.revokeObjectURL(voiceUrlRef.current);
      voiceUrlRef.current="";
    }
    voiceChunksRef.current=[];
    voiceSecondsRef.current=0;
    setVoiceBlob(null);setVoiceUrl("");setVoiceSeconds(0);setVoiceTranscript("");setRecording(false);
  };
  const stopVoice=()=>{
    clearVoiceTimer();
    const recorder=recorderRef.current;
    if(recorder&&recorder.state!=="inactive"){
      try{recorder.stop();}catch{}
    }
    releaseVoiceStream();
    setRecording(false);
  };
  const startVoice=async()=>{
    if(recording||busy)return;
    if(!navigator.mediaDevices?.getUserMedia||typeof MediaRecorder==="undefined"){
      toast.error("Este navegador no permite grabar audio directamente.");
      return;
    }
    try{
      resetVoice();
      const stream=await navigator.mediaDevices.getUserMedia({audio:true});
      streamRef.current=stream;
      const candidates=["audio/webm;codecs=opus","audio/webm","audio/mp4","audio/ogg;codecs=opus"];
      const mimeType=candidates.find(x=>MediaRecorder.isTypeSupported?.(x))||"";
      const recorder=new MediaRecorder(stream,mimeType?{mimeType}:{});
      recorderRef.current=recorder;
      voiceChunksRef.current=[];
      voiceSecondsRef.current=0;
      setVoiceSeconds(0);
      recorder.ondataavailable=(event)=>{
        if(event.data?.size)voiceChunksRef.current.push(event.data);
      };
      recorder.onstop=()=>{
        const type=recorder.mimeType||mimeType||"audio/webm";
        const blob=new Blob(voiceChunksRef.current,{type});
        if(blob.size){
          const url=URL.createObjectURL(blob);
          voiceUrlRef.current=url;
          setVoiceBlob(blob);
          setVoiceUrl(url);
          setVoiceSeconds(Math.max(1,voiceSecondsRef.current));
        }
        releaseVoiceStream();
        setRecording(false);
      };
      recorder.onerror=()=>toast.error("La grabación de audio se interrumpió.");
      recorder.start(250);
      setRecording(true);
      voiceTimerRef.current=window.setInterval(()=>{
        const next=Math.min(ORACLE_VOICE_LIMIT_SECONDS,voiceSecondsRef.current+1);
        voiceSecondsRef.current=next;
        setVoiceSeconds(next);
        if(next>=ORACLE_VOICE_LIMIT_SECONDS){
          clearVoiceTimer();
          if(recorder.state!=="inactive"){
            try{recorder.stop();}catch{}
          }
          releaseVoiceStream();
          setRecording(false);
        }
      },1000);
    }catch(e){
      releaseVoiceStream();
      toast.error("No fue posible acceder al micrófono. Revisa el permiso del navegador.");
    }
  };
  useEffect(()=>()=>{clearVoiceTimer();releaseVoiceStream();if(voiceUrlRef.current)URL.revokeObjectURL(voiceUrlRef.current);},[]);

  const load=useCallback(async()=>{
    try{
      const [a,m]=await Promise.all([api.get("/api/consultant/oracle/available"),api.get("/api/consultant/oracle/mine")]);
      setAvailable(a.data);setMine(m.data);
    }catch(e){toast.error(formatApiError(e));}
  },[]);
  useEffect(()=>{load();},[load]);

  const openGuide=async(item,claimFirst=true,existingClaim=null)=>{
    setBusy(true);
    try{
      // Always validate and reacquire a slot before opening a workspace.
      // A stale claim must never produce a success response with an expired deadline.
      const claim=(await api.post(`/api/consultant/oracle/${item.id}/claim`)).data;
      const g=(await api.get(`/api/consultant/oracle/${item.id}/guide`)).data;
      resetVoice();setAnswerMode("text");
      setWorkspace({...item,claim});setGuide(g);setAnswer("");setClaimIssue(false);setTab("workspace");await load();
    }catch(e){toast.error(formatApiError(e));}
    finally{setBusy(false);}
  };
  const reclaimWorkspace=async()=>{
    if(!workspace || busy) return;
    setBusy(true);
    try{
      const claim=(await api.post(`/api/consultant/oracle/${workspace.id}/claim`)).data;
      const fresh=(await api.get(`/api/consultant/oracle/${workspace.id}/guide`)).data;
      setWorkspace(prev=>({...prev,claim}));
      setGuide(fresh);
      setClaimIssue(false);
      toast.success("Reserva recuperada: tienes 3 horas para publicar. Tu audio y texto se conservaron.");
      await load();
    }catch(e){toast.error(formatApiError(e));}
    finally{setBusy(false);}
  };
  const submit=async()=>{
    if(answerMode==="text"&&answer.trim().length<40)return toast.error("La interpretación necesita un mínimo de desarrollo.");
    if(answerMode==="audio"&&!voiceBlob)return toast.error("Graba y revisa el audio antes de publicarlo.");
    if(answerMode==="audio"&&recording)return toast.error("Detén la grabación antes de publicarla.");
    setBusy(true);
    try{
      if(answerMode==="audio"){
        const form=new FormData();
        const mime=voiceBlob.type||"audio/webm";
        const ext=mime.includes("mp4")?"m4a":mime.includes("ogg")?"ogg":"webm";
        form.append("file",voiceBlob,`oraculo-${workspace.id}-${Date.now()}.${ext}`);
        form.append("duration_seconds",String(Math.max(1,voiceSeconds)));
        form.append("transcript",voiceTranscript.trim());
        await api.post(`/api/consultant/oracle/${workspace.id}/answer-audio`,form,{headers:{"Content-Type":"multipart/form-data"}});
        toast.success("Respuesta de voz publicada y ganancia registrada.");
      }else{
        await api.post(`/api/consultant/oracle/${workspace.id}/answer`,{answer:answer.trim()});
        toast.success("Interpretación publicada y ganancia registrada.");
      }
      setWorkspace(null);setGuide(null);setAnswer("");setAnswerMode("text");resetVoice();await load();setTab("mine");
    }catch(e){
      if(e?.response?.status===409) {
        setClaimIssue(true);
        toast.error("La reserva venció. Recupera el cupo sin perder la grabación y vuelve a publicar.");
      } else toast.error(formatApiError(e));
    }
    finally{setBusy(false);}
  };

  return <PageShell kicker="Consultor · Oráculo" title="INTERPRETACIONES DEL ORÁCULO" desc="Toma uno de los cuatro espacios disponibles. La biblioteca privada te orienta por carta, posición y orientación; la interpretación final es tuya." testId="oracle-consultant-page">
    {tab!=="workspace" ? <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
      <div className="flex-1"><OracleTabs items={[["available",`Disponibles · ${available.length}`],["mine","Mis interpretaciones"]]} active={tab} onChange={setTab} columns="grid-cols-2" /></div>
      <button type="button" disabled={busy} onClick={load} className="kd9-btn-outline min-h-11 px-3 py-2 rounded-lg text-[10px] inline-flex items-center justify-center gap-2 sm:mb-6"><RefreshCw size={13}/>Actualizar</button>
    </div> : <div className="mb-6">
      <button type="button" disabled={busy} onClick={()=>{resetVoice();setWorkspace(null);setGuide(null);setAnswer("");setClaimIssue(false);setAnswerMode("text");setTab("mine");}} className="kd9-btn-outline min-h-11 px-4 py-2 rounded-lg text-[10px]">← VOLVER A MIS INTERPRETACIONES</button>
    </div>}
    {tab==="available"&&<div className="space-y-4">{available.map(x=><div key={x.id} className="kd9-card p-4 sm:p-5">
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-mont text-[10px] uppercase" style={{color:"var(--kd9-text-soft)"}}>{x.deck_name} · {x.response_count}/{x.max_responses}</div>
          <p className="font-cormorant text-lg sm:text-xl mt-2 break-words">{x.question}</p>
        </div>
        <button type="button" disabled={busy} onClick={()=>openGuide(x,true)} className="kd9-btn-primary min-h-11 px-4 py-2 rounded-lg text-[10px] w-full lg:w-auto disabled:opacity-50">INTERPRETAR</button>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-sm mt-4">{x.cards.map(c=><CardFace key={c.position} card={c} deck={{theme:deckTheme(x.deck_slug)}} small fluid />)}</div>
    </div>)}{!available.length&&<div className="kd9-card p-8 sm:p-10 text-center">No hay preguntas con cupos disponibles.</div>}</div>}
    {tab==="mine"&&<div className="space-y-4">{mine.map(x=><div key={x.claim.id} className="kd9-card p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="font-cinzel kd9-gold-text flex-1 break-words">{x.consultation.question}</div>
        <span className="font-mont text-[9px] uppercase rounded-full border border-white/10 px-2 py-1 self-start sm:self-auto">{claimStatusLabel[x.claim.status] || x.claim.status}</span>
        {["claimed","expired"].includes(x.claim.status)&&<button type="button" disabled={busy} onClick={()=>openGuide(x.consultation,true,x.claim)} className="kd9-btn-primary min-h-11 px-3 py-2 rounded-lg text-[10px] w-full sm:w-auto">{x.claim.status==="expired"?"RETOMAR · 3 HORAS":"CONTINUAR"}</button>}
      </div>
      {x.claim.expires_at&&x.claim.status==="claimed"&&<div className="font-mont text-[9px] mt-3" style={{color:"var(--kd9-text-soft)"}}>Reserva hasta {new Date(x.claim.expires_at).toLocaleTimeString()}</div>}
      {x.claim.audio_url&&<div className="mt-4 rounded-xl border p-3 space-y-2" style={{borderColor:"var(--kd9-border)"}}>
        <div className="font-mont text-[9px] uppercase tracking-[.16em]" style={{color:"var(--kd9-text-soft)"}}>🎙️ Respuesta de voz</div>
        <audio controls preload="metadata" src={x.claim.audio_url} className="w-full" />
        {x.claim.transcript&&<p className="font-cormorant whitespace-pre-wrap break-words">{x.claim.transcript}</p>}
      </div>}
      {x.claim.answer_type!=="audio"&&x.claim.answer&&<p className="font-cormorant mt-3 whitespace-pre-wrap break-words">{x.claim.answer}</p>}
      {x.claim.earning_amount>0&&<div className="font-mont text-[10px] mt-3">Ganancia registrada: ⚡ {x.claim.earning_amount} KD</div>}
    </div>)}</div>}
    {tab==="workspace"&&guide&&<div className="space-y-5">
      <div className="kd9-card p-6">
        <div className="font-mont text-[10px] uppercase tracking-[.22em]" style={{color:"var(--kd9-text-soft)"}}>{guide.deck_name} · {guide.category}</div>
        <p className="font-cormorant text-2xl mt-2">{guide.question}</p>
        {workspace?.claim?.expires_at&&<div className="font-mont text-[10px] mt-3">Reserva activa hasta {new Date(workspace.claim.expires_at).toLocaleTimeString()}</div>}
        <div className="mt-4 rounded-xl border px-4 py-3 font-mont text-[10px]" style={{borderColor:"rgba(212,175,55,.24)",background:"rgba(212,175,55,.06)"}}>
          🔒 Esta guía es privada para el consultor. El usuario solo verá la interpretación que tú publiques.
        </div>
      </div>

      {guide.guide_enabled===false ? <div className="kd9-card p-4 sm:p-6 space-y-4" data-testid="oracle-guide-disabled">
        <p className="font-cormorant text-lg">Dirección ha desactivado la ayuda de significados para tu perfil. Puedes consultar las cartas y responder con tu propia interpretación.</p>
        <div className="grid grid-cols-3 gap-2 sm:gap-4">{guide.cards.map(c=><CardFace key={c.position} card={c} deck={{theme:deckTheme(workspace.deck_slug)}} small fluid />)}</div>
      </div> : <>
      <div className="grid lg:grid-cols-3 gap-4">{guide.cards.map(c=><div key={c.position} className="kd9-card p-4 sm:p-5 min-w-0">
        <div className="flex justify-center mb-4"><CardFace card={c} deck={{theme:deckTheme(workspace.deck_slug)}} /></div>
        <div className="flex items-center justify-between gap-2"><div className="font-cinzel kd9-gold-text">{c.position_label}</div><span className="font-mont text-[9px] uppercase">{c.orientation_label}</span></div>
        <div className="font-mont text-[10px] uppercase mt-3">Significado base</div>
        <p className="font-cormorant mt-1">{c.general_meaning}</p>
        <div className="font-mont text-[10px] uppercase mt-4">Significado en {c.position_label}</div>
        <p className="font-cormorant mt-1">{c.position_meaning}</p>
        <div className="flex flex-wrap gap-1 mt-3">{(c.keywords||[]).map(k=><span key={k} className="px-2 py-1 rounded-full border border-white/10 text-[9px]">{k}</span>)}</div>
        {c.thematic_note&&<div className="mt-4 rounded-xl border border-white/10 p-3"><div className="font-mont text-[9px] uppercase opacity-60">Nota temática</div><p className="font-cormorant mt-1 italic">{c.thematic_note}</p></div>}
        <div className="mt-4 rounded-xl border p-4" style={{borderColor:"rgba(99,215,255,.2)",background:"rgba(99,215,255,.045)"}}>
          <div className="font-mont text-[9px] uppercase tracking-[.18em]" style={{color:"#9bdff4"}}>Recomendación para interpretar</div>
          <p className="font-cormorant mt-2">{c.consultant_recommendation}</p>
        </div>
        <div className="mt-3 rounded-xl border p-4" style={{borderColor:"rgba(196,165,244,.18)",background:"rgba(196,165,244,.04)"}}>
          <div className="font-mont text-[9px] uppercase tracking-[.18em]" style={{color:"#c5a5f4"}}>Ejemplo de formulación</div>
          <p className="font-cormorant mt-2 italic">{c.sample_phrase}</p>
        </div>
      </div>)}</div>

      <div className="kd9-card p-4 sm:p-6 space-y-5">
        <div>
          <div className="font-cinzel kd9-gold-text text-xl">🔗 CONJUGACIÓN DE LAS TRES CARTAS</div>
          <p className="font-cormorant text-lg mt-2">{guide.combined_reading?.summary || guide.combined_prompt}</p>
        </div>
        {guide.combined_reading?.orientation_dynamic&&<div className="rounded-xl border border-white/10 p-4"><div className="font-mont text-[9px] uppercase opacity-60">Dinámica derecho / inversa</div><p className="font-cormorant mt-2">{guide.combined_reading.orientation_dynamic}</p></div>}
        {!!guide.combined_reading?.reading_path?.length&&<div><div className="font-mont text-[10px] uppercase mb-2">Cómo hilar la lectura</div><div className="space-y-2">{guide.combined_reading.reading_path.map((x,i)=><div key={i} className="rounded-xl border border-white/10 px-4 py-3 font-cormorant">{x}</div>)}</div></div>}
        {!!guide.combined_reading?.recommendations?.length&&<div><div className="font-mont text-[10px] uppercase mb-2">Recomendaciones para tu respuesta</div><div className="space-y-2">{guide.combined_reading.recommendations.map((x,i)=><div key={i} className="flex gap-3 rounded-xl border border-white/10 px-4 py-3"><span className="kd9-gold-text font-cinzel">{i+1}</span><p className="font-cormorant">{x}</p></div>)}</div></div>}
        {guide.combined_reading?.sample_opening&&<div className="rounded-xl border p-4" style={{borderColor:"rgba(212,175,55,.22)",background:"rgba(212,175,55,.05)"}}><div className="font-mont text-[9px] uppercase kd9-gold-text">Ejemplo para iniciar la interpretación</div><p className="font-cormorant text-lg mt-2 italic">{guide.combined_reading.sample_opening}</p></div>}
        <p className="font-mont text-[9px]" style={{color:"var(--kd9-text-soft)"}}>{guide.combined_reading?.caution || "Guía privada de apoyo. No sustituye tu criterio interpretativo."}</p>
      </div>

      </>}
      <div className="kd9-card p-4 sm:p-6 space-y-4">
        <div className="font-cinzel kd9-gold-text">TU INTERPRETACIÓN</div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" disabled={busy||recording} onClick={()=>setAnswerMode("text")} className={(answerMode==="text"?"kd9-btn-primary":"kd9-btn-outline")+" min-h-11 rounded-lg text-[10px]"}>✍️ TEXTO</button>
          <button type="button" disabled={busy} onClick={()=>setAnswerMode("audio")} className={(answerMode==="audio"?"kd9-btn-primary":"kd9-btn-outline")+" min-h-11 rounded-lg text-[10px]"}><Mic size={14} className="inline mr-2"/>AUDIO</button>
        </div>
        {answerMode==="text"?<>
          <textarea rows={10} maxLength={6000} value={answer} onChange={e=>setAnswer(e.target.value)} className="kd9-input w-full p-4 resize-y" placeholder="Construye la interpretación completa conectando pregunta, posiciones, orientación y conjugación de las tres cartas..." />
          <div className="font-mont text-[9px] text-right" style={{color:"var(--kd9-text-soft)"}}>{answer.length}/6000</div>
        </>:<div className="space-y-4" data-testid="oracle-voice-answer-panel">
          <div className="rounded-xl border p-4" style={{borderColor:"rgba(212,175,55,.24)",background:"rgba(212,175,55,.05)"}}>
            <div className="font-mont text-[10px] uppercase tracking-[.18em] kd9-gold-text">🎙️ Respuesta de voz del consultor</div>
            <p className="font-cormorant mt-2">Graba directamente desde KD9. Máximo 3 minutos. Podrás escucharla y regrabar antes de publicar.</p>
          </div>
          {recording&&<div className="rounded-xl border p-4 flex items-center justify-between gap-3" style={{borderColor:"rgba(239,68,68,.45)",background:"rgba(239,68,68,.08)"}}>
            <div><div className="font-mont text-[10px] uppercase" style={{color:"#EF4444"}}>● Grabando</div><div className="font-cinzel text-xl mt-1">{Math.floor(voiceSeconds/60)}:{String(voiceSeconds%60).padStart(2,"0")} / 3:00</div></div>
            <button type="button" onClick={stopVoice} className="kd9-btn-outline min-h-11 px-4 rounded-lg text-[10px] inline-flex items-center gap-2"><Square size={13}/>DETENER</button>
          </div>}
          {!recording&&!voiceBlob&&<button type="button" disabled={busy} onClick={startVoice} className="kd9-btn-primary min-h-12 w-full rounded-lg text-xs inline-flex items-center justify-center gap-2"><Mic size={16}/>INICIAR GRABACIÓN</button>}
          {!recording&&voiceBlob&&<div className="space-y-3">
            <div className="font-mont text-[9px] uppercase tracking-[.16em]" style={{color:"var(--kd9-text-soft)"}}>Vista previa · {Math.floor(voiceSeconds/60)}:{String(voiceSeconds%60).padStart(2,"0")}</div>
            <audio controls preload="metadata" src={voiceUrl} className="w-full" data-testid="oracle-voice-preview" />
            <div className="grid grid-cols-2 gap-2">
              <button type="button" disabled={busy} onClick={startVoice} className="kd9-btn-outline min-h-11 rounded-lg text-[10px] inline-flex items-center justify-center gap-2"><RotateCcw size={13}/>REGRABAR</button>
              <button type="button" disabled={busy} onClick={resetVoice} className="kd9-btn-outline min-h-11 rounded-lg text-[10px] inline-flex items-center justify-center gap-2"><Trash2 size={13}/>ELIMINAR</button>
            </div>
          </div>}
          <div>
            <label className="font-mont text-[9px] uppercase tracking-[.14em]" style={{color:"var(--kd9-text-soft)"}}>Transcripción o resumen (opcional)</label>
            <textarea rows={4} maxLength={6000} value={voiceTranscript} onChange={e=>setVoiceTranscript(e.target.value)} className="kd9-input w-full p-3 resize-y mt-2" placeholder="Puedes dejar por escrito los puntos esenciales de tu audio…" />
            <div className="font-mont text-[9px] text-right mt-1" style={{color:"var(--kd9-text-soft)"}}>{voiceTranscript.length}/6000</div>
          </div>
        </div>}
        {claimIssue&&<div className="rounded-xl border p-4 space-y-3" style={{borderColor:"rgba(245,158,11,.55)",background:"rgba(245,158,11,.08)"}} data-testid="oracle-claim-recovery">
          <p className="font-cormorant">Tu reserva expiró. El audio o texto que preparaste sigue aquí. Recupera un espacio disponible para obtener otras 3 horas; no se vuelve a cobrar al usuario.</p>
          <button type="button" onClick={reclaimWorkspace} disabled={busy} className="kd9-btn-outline min-h-11 px-4 rounded-lg text-xs w-full">RECUPERAR RESERVA SIN PERDER RESPUESTA</button>
        </div>}
        <div className="flex justify-end">
          <button type="button" disabled={busy || claimIssue || (answerMode==="text"?answer.trim().length<40:(!voiceBlob||recording))} onClick={submit} className="kd9-btn-primary min-h-11 px-6 py-3 rounded-lg text-xs w-full sm:w-auto disabled:opacity-50">
            {answerMode==="audio"?"PUBLICAR RESPUESTA DE VOZ":"PUBLICAR INTERPRETACIÓN"}
          </button>
        </div>
      </div>
    </div>}
  </PageShell>;
};

export const OracleDirector = () => {
  const [tab,setTab]=useState("resumen");
  const [settings,setSettings]=useState(null);
  const [decks,setDecks]=useState([]);
  const [methods,setMethods]=useState([]);
  const [consultations,setConsultations]=useState([]);
  const [editDeck,setEditDeck]=useState(null);
  const [cards,setCards]=useState([]);
  const [selectedCard,setSelectedCard]=useState(null);
  const [search,setSearch]=useState("");
  const [newDeck,setNewDeck]=useState({slug:"",name:"",description:"",source_deck_slug:"tarot-kd9",back_symbol:"✦",theme:"custom",allow_reversed:true,display_order:100,guide_note:""});
  const [busy,setBusy]=useState(false);

  const load=useCallback(async()=>{
    try{
      const [s,d,m,c]=await Promise.all([
        api.get("/api/director/oracle/settings"),api.get("/api/director/oracle/decks"),
        api.get("/api/director/oracle/methods"),api.get("/api/director/oracle/consultations"),
      ]);
      setSettings(s.data);setDecks(d.data);setMethods(m.data);setConsultations(c.data);
    }catch(e){toast.error(formatApiError(e));}
  },[]);
  useEffect(()=>{load();},[load]);

  const saveSettings=async()=>{setBusy(true);try{const {data}=await api.put("/api/director/oracle/settings",{enabled:!!settings.enabled,question_cost:Number(settings.question_cost||0),consultant_reward:Number(settings.consultant_reward||0),max_responses:Number(settings.max_responses||4),claim_minutes:180});setSettings(data);toast.success("Configuración del Oráculo actualizada.");}catch(e){toast.error(formatApiError(e));}finally{setBusy(false);}};
  const openDeck=async(d)=>{if(!d){setEditDeck(null);setSelectedCard(null);setCards([]);return;}setBusy(true);setEditDeck({...d});setSelectedCard(null);setCards([]);try{const {data}=await api.get(`/api/director/oracle/decks/${d.slug}/cards`);setCards(data.cards);setEditDeck({...data.deck});}catch(e){toast.error(formatApiError(e));}finally{setBusy(false);}};
  const editDeckFromList=async(d)=>{await openDeck(d);setTimeout(()=>document.getElementById("oracle-deck-editor")?.scrollIntoView({behavior:"smooth",block:"start"}),80);};
  const saveDeck=async()=>{setBusy(true);try{const {data}=await api.put(`/api/director/oracle/decks/${editDeck.slug}`,{name:editDeck.name,description:editDeck.description,back_symbol:editDeck.back_symbol,theme:editDeck.theme,allow_reversed:!!editDeck.allow_reversed,active:!!editDeck.active,display_order:Number(editDeck.display_order||0),guide_note:editDeck.guide_note||"",cover_image_url:editDeck.cover_image_url||null});setEditDeck({...data});toast.success("Tarot actualizado.");await load();}catch(e){toast.error(formatApiError(e));}finally{setBusy(false);}};
  const uploadDeckCover=async(file)=>{if(!file||!editDeck)return;setBusy(true);try{const form=new FormData();form.append("file",file);const {data}=await api.post(`/api/director/oracle/decks/${editDeck.slug}/cover`,form,{headers:{"Content-Type":"multipart/form-data"}});setEditDeck({...data.deck});toast.success("Portada principal del Tarot actualizada.");await load();}catch(e){toast.error(formatApiError(e));}finally{setBusy(false);}};
  const createDeck=async()=>{setBusy(true);try{await api.post("/api/director/oracle/decks",newDeck);toast.success("Nuevo Tarot creado en borrador con 78 cartas.");setNewDeck({...newDeck,slug:"",name:"",description:""});await load();}catch(e){toast.error(formatApiError(e));}finally{setBusy(false);}};
  const openLibrary=async(d)=>{setTab("biblioteca");await openDeck(d);};
  const saveCard=async()=>{if(!selectedCard)return;setBusy(true);try{const o=selectedCard.overrides||{};await api.put(`/api/director/oracle/decks/${editDeck.slug}/cards/${selectedCard.card_key}`,{display_name:selectedCard.display_name,image_url:selectedCard.image_url||"",art_symbol:selectedCard.art_symbol||editDeck.back_symbol,keywords_upright:(o.keywords_upright||selectedCard.base.keywords_upright||[]),keywords_reversed:(o.keywords_reversed||selectedCard.base.keywords_reversed||[]),upright:o.upright??"",reversed:o.reversed??"",root_upright:o.root_upright??"",root_reversed:o.root_reversed??"",present_upright:o.present_upright??"",present_reversed:o.present_reversed??"",projection_upright:o.projection_upright??"",projection_reversed:o.projection_reversed??"",recommendation_upright:o.recommendation_upright??"",recommendation_reversed:o.recommendation_reversed??"",root_recommendation_upright:o.root_recommendation_upright??"",root_recommendation_reversed:o.root_recommendation_reversed??"",present_recommendation_upright:o.present_recommendation_upright??"",present_recommendation_reversed:o.present_recommendation_reversed??"",projection_recommendation_upright:o.projection_recommendation_upright??"",projection_recommendation_reversed:o.projection_recommendation_reversed??"",sample_upright:o.sample_upright??"",sample_reversed:o.sample_reversed??"",love_note:o.love_note??"",work_note:o.work_note??"",money_note:o.money_note??"",growth_note:o.growth_note??""});toast.success("Carta, significados y recomendaciones actualizados.");await openDeck(editDeck);}catch(e){toast.error(formatApiError(e));}finally{setBusy(false);}};
  const modConsult=async(id,hidden)=>{setBusy(true);try{await api.put(`/api/director/oracle/consultations/${id}/moderation`,{hidden,reason:hidden?"Moderación Director":null});await load();toast.success(hidden?"Consulta oculta.":"Consulta visible.");}catch(e){toast.error(formatApiError(e));}finally{setBusy(false);}};
  const modResponse=async(id,hidden)=>{setBusy(true);try{await api.put(`/api/director/oracle/responses/${id}/moderation`,{hidden,reason:hidden?"Moderación Director":null});await load();toast.success(hidden?"Interpretación oculta.":"Interpretación visible.");}catch(e){toast.error(formatApiError(e));}finally{setBusy(false);}};

  const filteredCards=useMemo(()=>cards.filter(c=>(c.display_name+" "+c.card_key).toLowerCase().includes(search.toLowerCase())),[cards,search]);
  const patchOverride=(key,value)=>setSelectedCard(c=>({...c,overrides:{...(c.overrides||{}),[key]:value}}));

  return <PageShell kicker="Director KD9 · Gobierno" title="ORÁCULO KD9" desc="Controla economía, Tarot, biblioteca interpretativa, consultas y la expansión futura del Oráculo sin tocar código." testId="oracle-director-page">
    <div className="mb-6 space-y-3">
      <DirectorOracleTabs active={tab} onChange={setTab} />
      <div className="flex justify-end">
        <button type="button" disabled={busy} onClick={load} className="kd9-btn-outline min-h-11 px-4 py-2 rounded-xl text-[10px] inline-flex items-center justify-center gap-2 w-full sm:w-auto"><RefreshCw size={13}/>Actualizar gobierno</button>
      </div>
    </div>

    {tab==="resumen"&&<div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">{[
      ["Tarot activos",decks.filter(d=>d.active).length],["Cartas base",decks.reduce((a,d)=>a+(d.card_count||0),0)],["Consultas",consultations.length],["Costo actual",settings?.question_cost?`⚡ ${settings.question_cost} KD`:"Gratis"]
    ].map(([l,v])=><div key={l} className="kd9-card p-4 sm:p-6 min-w-0"><div className="font-mont text-[9px] sm:text-[10px] uppercase" style={{color:"var(--kd9-text-soft)"}}>{l}</div><div className="font-cinzel kd9-gold-text text-xl sm:text-3xl mt-2 break-words">{v}</div></div>)}</div>}

    {tab==="economia"&&settings&&<div className="kd9-card p-4 sm:p-6 max-w-3xl w-full">
      <div className="grid md:grid-cols-2 gap-4">
        <label className="font-mont text-xs">Costo de cada pregunta (KD)<input type="number" min="0" className="kd9-input w-full p-3 mt-2" value={settings.question_cost} onChange={e=>setSettings({...settings,question_cost:e.target.value})}/></label>
        <label className="font-mont text-xs">Ganancia por respuesta (KD)<input type="number" min="0" className="kd9-input w-full p-3 mt-2" value={settings.consultant_reward} onChange={e=>setSettings({...settings,consultant_reward:e.target.value})}/></label>
        <label className="font-mont text-xs">Máximo de consultores<input type="number" min="1" max="4" className="kd9-input w-full p-3 mt-2" value={settings.max_responses} onChange={e=>setSettings({...settings,max_responses:e.target.value})}/></label>
        <label className="font-mont text-xs">Tiempo por interpretación<input type="text" readOnly className="kd9-input w-full p-3 mt-2" value="3 horas (180 minutos)" /></label>
      </div>
      <label className="flex items-center gap-3 mt-5 font-mont text-xs"><input type="checkbox" checked={!!settings.enabled} onChange={e=>setSettings({...settings,enabled:e.target.checked})}/> Oráculo activo</label>
      <button type="button" disabled={busy} onClick={saveSettings} className="kd9-btn-primary min-h-11 px-5 py-3 rounded-lg text-xs mt-5 w-full sm:w-auto disabled:opacity-50"><Save size={13} className="inline mr-2"/>GUARDAR CONFIGURACIÓN</button>
    </div>}

    {tab==="mazos"&&<div className="space-y-6">
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">{decks.map(d=><div key={d.slug} className="kd9-card overflow-hidden"><DeckCover deck={d} className="h-32" /><div className="p-4"><div className="font-cinzel kd9-gold-text">{d.name}</div><div className="font-mont text-[10px] mt-1">{d.card_count} cartas · {d.active?"Activo":"Oculto"}</div><div className="grid grid-cols-2 gap-2 mt-3"><button type="button" disabled={busy} onClick={()=>editDeckFromList(d)} className="kd9-btn-outline min-h-11 px-3 py-2 rounded text-[9px] disabled:opacity-50">Editar</button><button type="button" disabled={busy} onClick={()=>openLibrary(d)} className="kd9-btn-outline min-h-11 px-3 py-2 rounded text-[9px] disabled:opacity-50">Biblioteca</button></div></div></div>)}</div>
      {editDeck&&<div id="oracle-deck-editor" className="kd9-card p-4 sm:p-6 scroll-mt-24">
        <div className="font-cinzel kd9-gold-text text-xl mb-5">EDITAR TAROT</div>
        <div className="grid gap-5 lg:grid-cols-[220px_1fr]">
          <div className="space-y-3">
            <DeckCover deck={editDeck} className="h-56 rounded-xl border" />
            <label className="kd9-btn-outline min-h-11 w-full cursor-pointer rounded-xl px-4 py-3 text-[10px] inline-flex items-center justify-center gap-2">
              <ImagePlus size={15}/>{busy ? "CARGANDO..." : editDeck.cover_image_url ? "CAMBIAR FOTO PRINCIPAL" : "SUBIR FOTO PRINCIPAL"}
              <input
                hidden
                type="file"
                accept="image/*"
                disabled={busy}
                onChange={async e=>{const file=e.target.files?.[0];await uploadDeckCover(file);e.target.value="";}}
              />
            </label>
            <p className="font-mont text-[9px] leading-relaxed" style={{color:"var(--kd9-text-soft)"}}>JPG, PNG o WEBP · máximo 8 MB. Esta imagen se usa como portada principal del mazo.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-3 min-w-0">
            <label className="font-mont text-[10px] uppercase">Nombre<input className="kd9-input p-3 mt-2" value={editDeck.name} onChange={e=>setEditDeck({...editDeck,name:e.target.value})}/></label>
            <label className="font-mont text-[10px] uppercase">Símbolo del reverso<input className="kd9-input p-3 mt-2" value={editDeck.back_symbol} onChange={e=>setEditDeck({...editDeck,back_symbol:e.target.value})}/></label>
            <label className="font-mont text-[10px] uppercase md:col-span-2">Descripción<textarea className="kd9-input p-3 mt-2 w-full resize-y" rows={3} value={editDeck.description} onChange={e=>setEditDeck({...editDeck,description:e.target.value})}/></label>
            <label className="font-mont text-[10px] uppercase md:col-span-2">Guía del mazo<textarea className="kd9-input p-3 mt-2 w-full resize-y" rows={4} value={editDeck.guide_note||""} onChange={e=>setEditDeck({...editDeck,guide_note:e.target.value})}/></label>
            <label className="font-mont text-[10px] uppercase">Orden<input type="number" className="kd9-input p-3 mt-2" value={editDeck.display_order} onChange={e=>setEditDeck({...editDeck,display_order:e.target.value})}/></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="kd9-card min-h-11 px-4 py-3 flex gap-3 items-center text-xs"><input type="checkbox" checked={!!editDeck.allow_reversed} onChange={e=>setEditDeck({...editDeck,allow_reversed:e.target.checked})}/> Permitir invertidas</label>
              <label className="kd9-card min-h-11 px-4 py-3 flex gap-3 items-center text-xs"><input type="checkbox" checked={!!editDeck.active} onChange={e=>setEditDeck({...editDeck,active:e.target.checked})}/> Visible para usuarios</label>
            </div>
          </div>
        </div>
        <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button type="button" disabled={busy} onClick={()=>setEditDeck(null)} className="kd9-btn-outline min-h-11 px-4 py-2 rounded-xl text-xs w-full sm:w-auto">Cerrar editor</button>
          <button type="button" disabled={busy} onClick={saveDeck} className="kd9-btn-primary min-h-11 px-5 py-3 rounded-xl text-xs w-full sm:w-auto disabled:opacity-50"><Save size={13} className="inline mr-2"/>GUARDAR TAROT</button>
        </div>
      </div>}
      <div className="kd9-card p-4 sm:p-6"><div className="font-cinzel kd9-gold-text text-xl mb-4">+ CREAR NUEVO TAROT</div><div className="grid md:grid-cols-2 gap-3"><input className="kd9-input p-3" placeholder="Identificador: tarot-nuevo" value={newDeck.slug} onChange={e=>setNewDeck({...newDeck,slug:e.target.value.toLowerCase().replace(/\s+/g,"-")})}/><input className="kd9-input p-3" placeholder="Nombre" value={newDeck.name} onChange={e=>setNewDeck({...newDeck,name:e.target.value})}/><textarea className="kd9-input p-3 md:col-span-2" placeholder="Descripción" value={newDeck.description} onChange={e=>setNewDeck({...newDeck,description:e.target.value})}/><select className="kd9-input p-3" value={newDeck.source_deck_slug} onChange={e=>setNewDeck({...newDeck,source_deck_slug:e.target.value})}>{decks.map(d=><option key={d.slug} value={d.slug}>Base: {d.name}</option>)}</select><input className="kd9-input p-3" placeholder="Símbolo reverso" value={newDeck.back_symbol} onChange={e=>setNewDeck({...newDeck,back_symbol:e.target.value})}/></div><button type="button" disabled={busy || !newDeck.slug || !newDeck.name || newDeck.description.trim().length<10} onClick={createDeck} className="kd9-btn-primary min-h-11 px-4 py-2 rounded-lg text-xs mt-4 w-full sm:w-auto disabled:opacity-50"><Plus size={13} className="inline mr-2"/>Crear en borrador</button></div>
    </div>}

    {tab==="biblioteca"&&<div className="grid lg:grid-cols-[320px_1fr] gap-5">
      <div className="space-y-3"><select className="kd9-input p-3 w-full" value={editDeck?.slug||""} onChange={e=>openDeck(decks.find(d=>d.slug===e.target.value))}><option value="">Selecciona Tarot</option>{decks.map(d=><option key={d.slug} value={d.slug}>{d.name}</option>)}</select><div className="relative"><Search size={14} className="absolute left-3 top-3.5"/><input className="kd9-input pl-9 pr-3 py-3 w-full" placeholder="Buscar carta" value={search} onChange={e=>setSearch(e.target.value)}/></div><div className="max-h-64 lg:max-h-[70vh] overflow-auto space-y-1 pr-1">{filteredCards.map(c=><button key={c.card_key} onClick={()=>setSelectedCard(JSON.parse(JSON.stringify(c)))} className="w-full min-h-11 text-left kd9-card px-3 py-2 text-xs">{c.display_name}<span className="block text-[9px] opacity-50">{c.card_key}</span></button>)}</div></div>
      <div className="min-w-0">{selectedCard?<div className="kd9-card p-4 sm:p-6 space-y-4"><div className="font-cinzel kd9-gold-text text-2xl">{selectedCard.display_name}</div><div className="grid md:grid-cols-2 gap-3"><input className="kd9-input p-3" value={selectedCard.display_name} onChange={e=>setSelectedCard({...selectedCard,display_name:e.target.value})}/><input className="kd9-input p-3" placeholder="URL de imagen de la carta" value={selectedCard.image_url||""} onChange={e=>setSelectedCard({...selectedCard,image_url:e.target.value})}/></div><div className="text-[10px] font-mont uppercase opacity-60">Si un campo queda vacío, hereda automáticamente la biblioteca base.</div>
      <div className="grid xl:grid-cols-2 gap-4">
      {[["upright","Significado general · Derecho"],["reversed","Significado general · Invertida"],["root_upright","Raíz · Derecho"],["root_reversed","Raíz · Invertida"],["present_upright","Energía actual · Derecho"],["present_reversed","Energía actual · Invertida"],["projection_upright","Proyección · Derecho"],["projection_reversed","Proyección · Invertida"],["recommendation_upright","Recomendación general · Derecho"],["recommendation_reversed","Recomendación general · Invertida"],["root_recommendation_upright","Recomendación Raíz · Derecho"],["root_recommendation_reversed","Recomendación Raíz · Invertida"],["present_recommendation_upright","Recomendación Energía actual · Derecho"],["present_recommendation_reversed","Recomendación Energía actual · Invertida"],["projection_recommendation_upright","Recomendación Proyección · Derecho"],["projection_recommendation_reversed","Recomendación Proyección · Invertida"],["sample_upright","Ejemplo de formulación · Derecho"],["sample_reversed","Ejemplo de formulación · Invertida"],["love_note","Nota Amor"],["work_note","Nota Trabajo"],["money_note","Nota Dinero"],["growth_note","Nota Crecimiento"]].map(([k,l])=><label key={k} className="block font-mont text-[10px] uppercase">{l}<textarea rows={3} className="kd9-input w-full p-3 mt-1 normal-case font-cormorant text-base" value={selectedCard.overrides?.[k]||""} onChange={e=>patchOverride(k,e.target.value)} placeholder={k==="upright"?selectedCard.base.upright:k==="reversed"?selectedCard.base.reversed:"Hereda significado base + contexto de posición"}/></label>)}
      </div>
      <button type="button" disabled={busy} onClick={saveCard} className="kd9-btn-primary min-h-11 px-5 py-3 rounded-lg text-xs w-full sm:w-auto disabled:opacity-50"><Save size={13} className="inline mr-2"/>GUARDAR CARTA</button></div>:<div className="kd9-card p-10 text-center">Selecciona un Tarot y una carta para editar su biblioteca.</div>}</div>
    </div>}

    {tab==="consultas"&&<div className="space-y-4">{consultations.map(x=><ConsultationCard key={x.id} item={x} directorActions={{consultation:modConsult,response:modResponse,busy}} />)}{!consultations.length&&<div className="kd9-card p-10 text-center">Aún no hay consultas.</div>}</div>}

    {tab==="metodos"&&<div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">{methods.map(m=><div key={m.slug} className="kd9-card p-6"><div className="text-3xl">{m.slug==="tarot"?"🃏":m.slug==="numerologia"?"🔢":m.slug==="dados"?"🎲":"✨"}</div><div className="font-cinzel kd9-gold-text text-xl mt-3">{m.name}</div><div className="font-mont text-[10px] mt-2">{m.implemented?(m.active?"Activo":"Desactivado"):"Preparado · Próximamente"}</div></div>)}</div>}
  </PageShell>;
};

export default OracleUser;
